/**
 * ModelCategoryPicker — Shared combobox for AI model category selection.
 *
 * Used in three contexts:
 * 1. Tenant AI settings — "Platform Default" option (null = use sysadmin/config default)
 * 2. Agent processing tab — "Tenant Default" option (null = use tenant skill default)
 * 3. Chat inline selector — compact variant, no default option
 *
 * Shows category name, provider, resolved model, reasoning level, and tier badge.
 * Supports match-as-you-type search via Command component.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, ChevronDown, Cpu } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { filterAndSortBySearch } from '../../utils/searchScore';
import type { ModelCategory } from '../../hooks/useAvailableModelCategories';

/** What the "null" value means — determines the default option label */
export type DefaultMode = 'platform' | 'tenant' | 'none';

/** Display variant */
export type PickerVariant = 'default' | 'compact';

export interface ModelCategoryPickerProps {
  /** Selected category ID, or null for "use default" */
  value: string | null;
  /** Called when selection changes (null = default selected) */
  onChange: (categoryId: string | null) => void;
  /** Available categories to display */
  categories: ModelCategory[];
  /** What the null/default option means */
  defaultMode?: DefaultMode;
  /** Display variant: 'default' for form fields, 'compact' for inline chat */
  variant?: PickerVariant;
  /** Optional label above the picker */
  label?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether the label should show as changed (blue highlight) */
  isChanged?: boolean;
  /** Name of the category the default resolves to (e.g., "Claude Sonnet") */
  defaultResolvedName?: string;
  /** Specific model the default resolves to (e.g., "gpt-5-mini") — shown beneath picker */
  defaultResolvedModel?: string;
  /** Fallback label when value is null and defaultMode is 'none' (e.g., effective model name in chat) */
  fallbackLabel?: string;
  /**
   * Categories incompatible with the current use case, mapped to the MIME types
   * the category's provider fails to accept. Entries here are rendered disabled
   * with a short explanation beneath the category name.
   */
  incompatibleCategories?: Record<string, string[]>;
}

function formatMimeTypeLabel(mime: string): string {
  switch (mime) {
    case 'application/pdf': return 'PDF';
    case 'image/jpeg': return 'JPEG';
    case 'image/png': return 'PNG';
    case 'image/gif': return 'GIF';
    case 'image/webp': return 'WebP';
    default: {
      const slash = mime.lastIndexOf('/');
      return slash >= 0 ? mime.slice(slash + 1).toUpperCase() : mime;
    }
  }
}

function getTierVariant(tier: string | null): 'default' | 'secondary' | 'outline' {
  switch (tier) {
    case 'premium': return 'secondary';
    case 'standard': return 'default';
    default: return 'outline';
  }
}

function formatReasoningLevel(level: string | null, t: (key: string) => string): string | null {
  if (!level) return null;
  return t(`common:modelPicker.reasoning.${level}`);
}

export function ModelCategoryPicker({
  value,
  onChange,
  categories,
  defaultMode = 'none',
  variant = 'default',
  label,
  disabled = false,
  className,
  isChanged = false,
  defaultResolvedName,
  defaultResolvedModel,
  fallbackLabel,
  incompatibleCategories,
}: ModelCategoryPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCategory = useMemo(
    () => categories.find(c => c.id === value) ?? null,
    [categories, value],
  );

  // Filter categories by search text
  const filteredCategories = useMemo(
    () =>
      filterAndSortBySearch(categories, search, (c) =>
        [c.name, c.provider_name, c.resolved_model, c.description].filter(Boolean).join(' '),
      ),
    [categories, search],
  );

  // Group filtered categories by provider
  const groupedCategories = useMemo(() => {
    const groups: Record<string, ModelCategory[]> = {};
    for (const cat of filteredCategories) {
      const key = cat.provider_name || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(cat);
    }
    return groups;
  }, [filteredCategories]);

  const handleSelect = (categoryId: string | null) => {
    onChange(categoryId);
    setOpen(false);
    setSearch('');
  };

  // Build display text for the trigger button
  const triggerDisplay = useMemo(() => {
    if (value === null && defaultMode !== 'none') {
      const defaultLabel = defaultMode === 'platform'
        ? t('modelPicker.platformDefault')
        : t('modelPicker.tenantDefault');
      // Show resolved name when available: "Platform Default (Claude Sonnet)"
      const label = defaultResolvedName
        ? `${defaultLabel} (${defaultResolvedName})`
        : defaultLabel;
      return { label, isDefault: true };
    }
    if (selectedCategory) {
      return { label: selectedCategory.name, isDefault: false };
    }
    if (fallbackLabel) {
      return { label: fallbackLabel, isDefault: false };
    }
    return { label: t('modelPicker.selectModel'), isDefault: false };
  }, [value, defaultMode, selectedCategory, t, defaultResolvedName, fallbackLabel]);

  // Resolved info line (shown below trigger or inside it for compact)
  const resolvedInfo = useMemo(() => {
    const cat = selectedCategory;
    if (!cat && value === null && defaultMode !== 'none') {
      // Using the default — show the specific resolved model so the user can see
      // which concrete model the default currently points to.
      return defaultResolvedModel ?? null;
    }
    if (!cat) return null;
    const parts: string[] = [];
    if (cat.resolved_model) parts.push(cat.resolved_model);
    const reasoning = formatReasoningLevel(cat.reasoning_level, t);
    if (reasoning) parts.push(reasoning);
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [selectedCategory, value, defaultMode, defaultResolvedModel, t]);

  // ── Compact variant (inline chat style) ──
  if (variant === 'compact') {
    // For compact, when no explicit selection, prefer the resolved name (e.g., "Sonnet 4.6")
    // over the verbose "Tenant Default (Sonnet 4.6)" produced by triggerDisplay.
    const compactLabel = (value === null && fallbackLabel)
      ? fallbackLabel
      : triggerDisplay.label;
    const shortName = compactLabel.replace(/^Claude\s+/i, '');
    const canOpen = !disabled && categories.length > 0;
    const defaultLabelText = defaultMode === 'platform'
      ? t('modelPicker.platformDefault')
      : t('modelPicker.tenantDefault');

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'inline-flex items-center gap-1 text-xs text-muted-foreground hover:opacity-100 transition-opacity whitespace-nowrap',
              canOpen ? 'cursor-pointer opacity-80' : 'cursor-default opacity-60',
              className,
            )}
            disabled={!canOpen}
          >
            <Cpu className="h-3.5 w-3.5 shrink-0" />
            <span>{shortName}</span>
            {canOpen && <ChevronDown className="h-3 w-3 shrink-0" />}
          </button>
        </PopoverTrigger>
        {canOpen && (
          <PopoverContent className="w-80 p-0" align="center">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={t('modelPicker.searchPlaceholder')}
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>{t('modelPicker.noResults')}</CommandEmpty>

                {defaultMode !== 'none' && (
                  <>
                    <CommandGroup>
                      <CommandItem
                        value="__default__"
                        onSelect={() => handleSelect(null)}
                      >
                        <div className="flex items-center gap-2 w-full min-w-0">
                          <Check className={cn('h-4 w-4 shrink-0', value === null ? 'opacity-100' : 'opacity-0')} />
                          <div className="flex-1 min-w-0">
                            <span className="text-muted-foreground">{defaultLabelText}</span>
                            {fallbackLabel && (
                              <span className="text-muted-foreground text-xs ml-1">
                                ({fallbackLabel})
                              </span>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}

                {Object.entries(groupedCategories).map(([providerName, cats]) => (
                  <CommandGroup key={providerName} heading={providerName}>
                    {cats.map(cat => {
                      const missing = incompatibleCategories?.[cat.id];
                      const isIncompatible = !!missing?.length;
                      const isUnavailable = cat.available === false;
                      const isDisabled = isIncompatible || isUnavailable;
                      return (
                        <CommandItem
                          key={cat.id}
                          value={cat.id}
                          disabled={isDisabled}
                          onSelect={() => {
                            if (!isDisabled) handleSelect(cat.id);
                          }}
                        >
                          <CategoryItemContent
                            category={cat}
                            isSelected={cat.id === value}
                            missingMimeTypes={missing}
                            isUnavailable={isUnavailable}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    );
  }

  // ── Default variant (form field style) ──
  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label className={cn('mb-1.5 block', isChanged && 'text-blue-600')}>
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal',
              triggerDisplay.isDefault && 'text-muted-foreground',
            )}
          >
            <span className="truncate">{triggerDisplay.label}</span>
            <ChevronDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('modelPicker.searchPlaceholder')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>{t('modelPicker.noResults')}</CommandEmpty>

              {/* Default option */}
              {defaultMode !== 'none' && (
                <>
                  <CommandGroup>
                    <CommandItem
                      value="__default__"
                      onSelect={() => handleSelect(null)}
                    >
                      <div className="flex items-center gap-2 w-full min-w-0">
                        <Check className={cn('h-4 w-4 shrink-0', value === null ? 'opacity-100' : 'opacity-0')} />
                        <div className="flex-1 min-w-0">
                          <span className="text-muted-foreground">
                            {defaultMode === 'platform'
                              ? t('modelPicker.platformDefault')
                              : t('modelPicker.tenantDefault')
                            }
                          </span>
                          {defaultResolvedName && (
                            <span className="text-muted-foreground text-xs ml-1">
                              ({defaultResolvedName})
                            </span>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {Object.entries(groupedCategories).map(([providerName, cats]) => (
                <CommandGroup key={providerName} heading={providerName}>
                  {cats.map(cat => {
                    const missing = incompatibleCategories?.[cat.id];
                    const isIncompatible = !!missing?.length;
                    const isUnavailable = cat.available === false;
                    const isDisabled = isIncompatible || isUnavailable;
                    return (
                      <CommandItem
                        key={cat.id}
                        value={cat.id}
                        disabled={isDisabled}
                        onSelect={() => {
                          if (!isDisabled) handleSelect(cat.id);
                        }}
                      >
                        <CategoryItemContent
                          category={cat}
                          isSelected={cat.id === value}
                          missingMimeTypes={missing}
                          isUnavailable={isUnavailable}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Resolved model info below the picker */}
      {resolvedInfo && (
        <p className="mt-1 text-xs text-muted-foreground">{resolvedInfo}</p>
      )}
    </div>
  );
}

// ── Shared item renderer ──

function CategoryItemContent({
  category,
  isSelected,
  missingMimeTypes,
  isUnavailable = false,
}: {
  category: ModelCategory;
  isSelected: boolean;
  missingMimeTypes?: string[];
  isUnavailable?: boolean;
}) {
  const { t } = useTranslation('common');
  const reasoning = formatReasoningLevel(category.reasoning_level, t);
  const isIncompatible = !!missingMimeTypes?.length;

  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      <Check className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{category.name}</span>
          {category.tier && (
            <Badge variant={getTierVariant(category.tier)} className="text-[10px] px-1 py-0 shrink-0">
              {category.tier}
            </Badge>
          )}
          {(isIncompatible || isUnavailable) && (
            <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {category.resolved_model && (
            <span className="truncate">{category.resolved_model}</span>
          )}
          {reasoning && (
            <>
              <span>·</span>
              <span className="shrink-0">{reasoning}</span>
            </>
          )}
        </div>
        {isIncompatible && (
          <p className="mt-0.5 text-xs text-warning">
            {t('modelPicker.incompatibleMimeTypes', {
              types: missingMimeTypes!.map(formatMimeTypeLabel).join(', '),
            })}
          </p>
        )}
        {!isIncompatible && isUnavailable && (
          <p className="mt-0.5 text-xs text-warning">
            {t('modelPicker.unavailable')}
          </p>
        )}
      </div>
    </div>
  );
}
